import { API_BASE } from "./config/api";
// src/AMCSchemeReport.js

import React, { useEffect, useMemo, useState } from "react";

import {
  Card,
  Table,
  Typography,
  Spin,
  Alert,
  Button,
  Space,
  Input,
  Grid,
} from "antd";

import {
  PlusSquareOutlined,
  MinusSquareOutlined,
  BankOutlined,
  FundOutlined,
  UserOutlined,
  SearchOutlined,
  FolderOpenOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;


// ============================================================
// API BASE
// ============================================================




// ============================================================
// MONEY FORMATTER
// ============================================================

const formatMoney = (value) => {

  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "₹0.00";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};


// ============================================================
// RETURN DISPLAY
// ============================================================

const ReturnValue = ({ value, percent = false }) => {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return <Text>-</Text>;
  }

  const num = Number(value);

  if (!Number.isFinite(num)) {
    return <Text>-</Text>;
  }

  const displayValue = percent
    ? `${num.toFixed(2)}%`
    : formatMoney(num);

  return (
    <Text
      style={{
        color:
          num > 0
            ? "#389e0d"
            : num < 0
            ? "#cf1322"
            : undefined,
        fontWeight: 600,
      }}
    >
      {displayValue}
    </Text>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

function AMCSchemeReport() {

  // ==========================================================
  // RESPONSIVE / MOBILE
  // ==========================================================
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const pagePadding = isMobile ? 6 : isTablet ? 12 : 24;
  const nestedPad = isMobile ? 4 : isTablet ? 8 : 16;
  const deepNestedPad = isMobile ? 2 : isTablet ? 6 : 14;

  // Drill-down navigation: each level replaces the report workspace instead of nesting tables.
  const [drillView, setDrillView] = useState({ level: "amcs" });
  useEffect(() => {
    const onPop = (e) => setDrillView(e.state?.kgAmcDrill || { level: "amcs" });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const pushDrill = (next) => {
    window.history.pushState({ ...window.history.state, kgAmcDrill: next }, "");
    setDrillView(next);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const drillBack = () => window.history.back();


  // ==========================================================
  // ARN
  // ==========================================================

  const arn =
    localStorage.getItem("arn_number") || "";


  // ==========================================================
  // SEARCH
  // ==========================================================

  const [searchText, setSearchText] =
    useState("");


  // ==========================================================
  // AMC
  // ==========================================================

  const [amcData, setAmcData] =
    useState([]);

  const [amcLoading, setAmcLoading] =
    useState(false);

  const [amcError, setAmcError] =
    useState("");


  // ==========================================================
  // EXPANSION
  // ==========================================================

  const [expandedAMCs, setExpandedAMCs] =
    useState({});

  const [expandedSchemes, setExpandedSchemes] =
    useState({});

  // Client -> Folio expansion
  const [expandedClients, setExpandedClients] =
    useState({});


  // ==========================================================
  // SCHEME CACHE
  // ==========================================================

  const [schemeData, setSchemeData] =
    useState({});

  const [schemeLoading, setSchemeLoading] =
    useState({});

  const [schemeErrors, setSchemeErrors] =
    useState({});


  // ==========================================================
  // CLIENT CACHE
  // ==========================================================

  const [clientData, setClientData] =
    useState({});

  const [clientLoading, setClientLoading] =
    useState({});

  const [clientErrors, setClientErrors] =
    useState({});


  // ==========================================================
  // FOLIO CACHE
  // ==========================================================

  const [folioData, setFolioData] =
    useState({});

  const [folioLoading, setFolioLoading] =
    useState({});

  const [folioErrors, setFolioErrors] =
    useState({});


  // ==========================================================
  // TRANSACTION CACHE / FOLIO -> TRANSACTIONS
  // ==========================================================

  const [expandedFolios, setExpandedFolios] =
    useState({});

  const [transactionData, setTransactionData] =
    useState({});

  const [transactionLoading, setTransactionLoading] =
    useState({});

  const [transactionErrors, setTransactionErrors] =
    useState({});


  // ==========================================================
  // INITIAL AMC LOAD
  // ==========================================================

  useEffect(() => {

    if (!arn) {

      setAmcError(
        "ARN number not found in login session."
      );

      return;
    }

    loadAMCData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arn]);


  // ==========================================================
  // LOAD AMC
  // ==========================================================

  const loadAMCData = async () => {

    try {

      setAmcLoading(true);
      setAmcError("");

      const url =
        `${API_BASE}/get_amc_wise_data.php` +
        `?arn=${encodeURIComponent(arn)}`;

      const response =
        await fetch(url);

      if (!response.ok) {

        throw new Error(
          `HTTP Error ${response.status}`
        );
      }

      const result =
        await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      setAmcData(
        Array.isArray(result.data)
          ? result.data
          : []
      );

    } catch (error) {

      console.error(
        "AMC DATA ERROR:",
        error
      );

      setAmcError(
        error.message ||
        "Unable to load AMC data."
      );

    } finally {

      setAmcLoading(false);
    }
  };


  // ==========================================================
  // AMC TOGGLE
  // ==========================================================

  const toggleAMC = async (amcName) => {

    const currentlyExpanded =
      !!expandedAMCs[amcName];

    if (currentlyExpanded) {

      setExpandedAMCs((prev) => ({
        ...prev,
        [amcName]: false,
      }));

      return;
    }

    setExpandedAMCs((prev) => ({
      ...prev,
      [amcName]: true,
    }));

    if (
      Object.prototype.hasOwnProperty.call(
        schemeData,
        amcName
      )
    ) {
      return;
    }

    await loadSchemeData(amcName);
  };


  // ==========================================================
  // LOAD SCHEMES
  // ==========================================================

  const loadSchemeData =
    async (amcName) => {

      try {

        setSchemeLoading((prev) => ({
          ...prev,
          [amcName]: true,
        }));

        setSchemeErrors((prev) => ({
          ...prev,
          [amcName]: "",
        }));

        const url =
          `${API_BASE}/get_scheme_wise_data.php` +
          `?arn=${encodeURIComponent(arn)}` +
          `&amc_name=${encodeURIComponent(amcName)}`;

        const response =
          await fetch(url);

        if (!response.ok) {

          throw new Error(
            `HTTP Error ${response.status}`
          );
        }

        const result =
          await response.json();

        if (result.error) {
          throw new Error(result.error);
        }

        setSchemeData((prev) => ({
          ...prev,

          [amcName]:
            Array.isArray(result.data)
              ? result.data
              : [],
        }));

      } catch (error) {

        console.error(
          "SCHEME DATA ERROR:",
          error
        );

        setSchemeErrors((prev) => ({
          ...prev,

          [amcName]:
            error.message ||
            "Unable to load Scheme data.",
        }));

      } finally {

        setSchemeLoading((prev) => ({
          ...prev,
          [amcName]: false,
        }));
      }
    };


  // ==========================================================
  // SCHEME TOGGLE
  // ==========================================================

  const toggleScheme =
    async (schemeCode) => {

      const key =
        String(schemeCode);

      const currentlyExpanded =
        !!expandedSchemes[key];

      if (currentlyExpanded) {

        setExpandedSchemes((prev) => ({
          ...prev,
          [key]: false,
        }));

        return;
      }

      setExpandedSchemes((prev) => ({
        ...prev,
        [key]: true,
      }));

      if (
        Object.prototype.hasOwnProperty.call(
          clientData,
          key
        )
      ) {
        return;
      }

      await loadClientData(
        schemeCode
      );
    };


  // ==========================================================
  // LOAD CLIENTS
  // ==========================================================

  const loadClientData =
    async (schemeCode) => {

      const key =
        String(schemeCode);

      try {

        setClientLoading((prev) => ({
          ...prev,
          [key]: true,
        }));

        setClientErrors((prev) => ({
          ...prev,
          [key]: "",
        }));

        const url =
          `${API_BASE}/get_scheme_client_data.php` +
          `?arn=${encodeURIComponent(arn)}` +
          `&scheme_code=${encodeURIComponent(schemeCode)}`;

        const response =
          await fetch(url);

        if (!response.ok) {

          throw new Error(
            `HTTP Error ${response.status}`
          );
        }

        const result =
          await response.json();

        if (result.error) {
          throw new Error(result.error);
        }

        setClientData((prev) => ({
          ...prev,

          [key]:
            Array.isArray(result.data)
              ? result.data
              : [],
        }));

      } catch (error) {

        console.error(
          "CLIENT DATA ERROR:",
          error
        );

        setClientErrors((prev) => ({
          ...prev,

          [key]:
            error.message ||
            "Unable to load Client data.",
        }));

      } finally {

        setClientLoading((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    };


  // ==========================================================
  // CLIENT UNIQUE KEY
  // ==========================================================

  const getClientKey =
    (schemeCode, pan) =>
      `${String(schemeCode)}-${String(pan)}`;


  // ==========================================================
  // CLIENT TOGGLE -> FOLIO
  // ==========================================================

  const toggleClient =
    async (schemeCode, pan) => {

      const key =
        getClientKey(
          schemeCode,
          pan
        );

      const currentlyExpanded =
        !!expandedClients[key];

      // COLLAPSE
      if (currentlyExpanded) {

        setExpandedClients((prev) => ({
          ...prev,
          [key]: false,
        }));

        return;
      }

      // EXPAND
      setExpandedClients((prev) => ({
        ...prev,
        [key]: true,
      }));

      // ALREADY LOADED
      if (
        Object.prototype.hasOwnProperty.call(
          folioData,
          key
        )
      ) {
        return;
      }

      await loadFolioData(
        schemeCode,
        pan
      );
    };


  // ==========================================================
  // LOAD FOLIO DATA
  // ==========================================================

  const loadFolioData =
    async (schemeCode, pan) => {

      const key =
        getClientKey(
          schemeCode,
          pan
        );

      try {

        setFolioLoading((prev) => ({
          ...prev,
          [key]: true,
        }));

        setFolioErrors((prev) => ({
          ...prev,
          [key]: "",
        }));

        const url =
          `${API_BASE}/get_scheme_folio_data.php` +
          `?arn=${encodeURIComponent(arn)}` +
          `&pan=${encodeURIComponent(pan)}` +
          `&scheme_code=${encodeURIComponent(schemeCode)}`;

        const response =
          await fetch(url);

        if (!response.ok) {

          throw new Error(
            `HTTP Error ${response.status}`
          );
        }

        const result =
          await response.json();

        if (result.error) {
          throw new Error(result.error);
        }

        setFolioData((prev) => ({
          ...prev,

          [key]:
            Array.isArray(result.data)
              ? result.data
              : [],
        }));

      } catch (error) {

        console.error(
          "FOLIO DATA ERROR:",
          error
        );

        setFolioErrors((prev) => ({
          ...prev,

          [key]:
            error.message ||
            "Unable to load Folio data.",
        }));

      } finally {

        setFolioLoading((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    };


  // ==========================================================
  // FOLIO UNIQUE KEY
  // ==========================================================

  const getFolioKey =
    (schemeCode, pan, folioNumber) =>
      `${String(schemeCode)}-${String(pan)}-${String(folioNumber)}`;


  // ==========================================================
  // FOLIO TOGGLE -> TRANSACTIONS
  // ==========================================================

  const toggleFolio =
    async (schemeCode, pan, folioNumber) => {

      const key =
        getFolioKey(
          schemeCode,
          pan,
          folioNumber
        );

      const currentlyExpanded =
        !!expandedFolios[key];

      // COLLAPSE
      if (currentlyExpanded) {

        setExpandedFolios((prev) => ({
          ...prev,
          [key]: false,
        }));

        return;
      }

      // EXPAND
      setExpandedFolios((prev) => ({
        ...prev,
        [key]: true,
      }));

      // ALREADY LOADED
      if (
        Object.prototype.hasOwnProperty.call(
          transactionData,
          key
        )
      ) {
        return;
      }

      await loadTransactionData(
        schemeCode,
        pan,
        folioNumber
      );
    };


  // ==========================================================
  // LOAD TRANSACTIONS
  //
  // IMPORTANT:
  // Existing get_transactions.php API is used AS-IS.
  //
  // Filters:
  // ARN + PAN + FOLIO + SCHEME CODE
  // ==========================================================

  const loadTransactionData =
    async (
      schemeCode,
      pan,
      folioNumber
    ) => {

      const key =
        getFolioKey(
          schemeCode,
          pan,
          folioNumber
        );

      try {

        setTransactionLoading((prev) => ({
          ...prev,
          [key]: true,
        }));

        setTransactionErrors((prev) => ({
          ...prev,
          [key]: "",
        }));

        const url =
          `${API_BASE}/get_transactions.php` +
          `?pan=${encodeURIComponent(pan)}` +
          `&folio_number=${encodeURIComponent(folioNumber)}` +
          `&scheme_code=${encodeURIComponent(schemeCode)}` +
          `&arn_no=${encodeURIComponent(arn)}`;

        const response =
          await fetch(url);

        if (!response.ok) {

          throw new Error(
            `HTTP Error ${response.status}`
          );
        }

        const result =
          await response.json();

        if (result.error) {
          throw new Error(result.error);
        }

        setTransactionData((prev) => ({
          ...prev,

          [key]:
            Array.isArray(result.transactions)
              ? result.transactions
              : [],
        }));

      } catch (error) {

        console.error(
          "TRANSACTION DATA ERROR:",
          error
        );

        setTransactionErrors((prev) => ({
          ...prev,

          [key]:
            error.message ||
            "Unable to load transaction data.",
        }));

      } finally {

        setTransactionLoading((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    };
  // ==========================================================
  // GLOBAL AMC FILTER
  // ==========================================================

  const filteredAmcData =
    useMemo(() => {

      const q =
        searchText
          .trim()
          .toLowerCase();

      if (!q) {
        return amcData;
      }

      return amcData.filter((row) => {

        const amcText = [
          row.amc_name,
          row.cost_amount,
          row.current_value,
          row.profit_loss,
          row.absolute_return,
          row.xirr,
        ]
          .map((v) =>
            String(v ?? "")
              .toLowerCase()
          )
          .join(" ");

        if (amcText.includes(q)) {
          return true;
        }

        const schemes =
          schemeData[row.amc_name] || [];

        return schemes.some((scheme) => {

          const schemeText = [
            scheme.scheme_name,
            scheme.scheme_code,
            scheme.cost_amount,
            scheme.current_value,
            scheme.profit_loss,
            scheme.absolute_return,
            scheme.xirr,
          ]
            .map((v) =>
              String(v ?? "")
                .toLowerCase()
            )
            .join(" ");

          if (schemeText.includes(q)) {
            return true;
          }

          const clients =
            clientData[
              String(scheme.scheme_code)
            ] || [];

          return clients.some(
            (client) => {

              const clientText = [
                client.invester_name,
                client.pan,
                client.cost_amount,
                client.current_value,
                client.profit_loss,
                client.absolute_return,
                client.xirr,
              ]
                .map((v) =>
                  String(v ?? "")
                    .toLowerCase()
                )
                .join(" ");

              if (clientText.includes(q)) {
                return true;
              }

              const folioKey =
                getClientKey(
                  scheme.scheme_code,
                  client.pan
                );

              const folios =
                folioData[folioKey] || [];

              return folios.some(
                (folio) => {

                  const folioText = [
                    folio.folio_number,
                    folio.purchase_units,
                    folio.purchase_amount,
                    folio.redemption_units,
                    folio.redemption_amount,
                    folio.balance_units,
                    folio.balance_value,
                    folio.profit_loss,
                    folio.absolute_return,
                    folio.xirr,
                  ]
                    .map((v) =>
                      String(v ?? "")
                        .toLowerCase()
                    )
                    .join(" ");

                  if (folioText.includes(q)) {
                    return true;
                  }

                  const txKey =
                    getFolioKey(
                      scheme.scheme_code,
                      client.pan,
                      folio.folio_number
                    );

                  const transactions =
                    transactionData[txKey] || [];

                  return transactions.some(
                    (tx) => {

                      const txText = [
                        tx.tran_date,
                        tx.nav_date,
                        tx.tran_no,
                        tx.units,
                        tx.amount,
                        tx.nav,
                        tx.tran_type,
                        tx.tran_description,
                        tx.tran_sign,
                        tx.remarks,
                        tx.balance_units,
                      ]
                        .map((v) =>
                          String(v ?? "")
                            .toLowerCase()
                        )
                        .join(" ");

                      return txText.includes(q);
                    }
                  );
                }
              );
            }
          );
        });
      });

    }, [
      amcData,
      searchText,
      schemeData,
      clientData,
      folioData,
      transactionData,
    ]);


  // ==========================================================
  // SCHEME FILTER
  // ==========================================================

  const getFilteredSchemes =
    (amcName) => {

      const rows =
        schemeData[amcName] || [];

      const q =
        searchText
          .trim()
          .toLowerCase();

      if (!q) {
        return rows;
      }

      return rows.filter((scheme) => {

        const schemeText = [
          scheme.scheme_name,
          scheme.scheme_code,
          scheme.cost_amount,
          scheme.current_value,
          scheme.profit_loss,
          scheme.absolute_return,
          scheme.xirr,
        ]
          .map((v) =>
            String(v ?? "")
              .toLowerCase()
          )
          .join(" ");

        if (schemeText.includes(q)) {
          return true;
        }

        const clients =
          clientData[
            String(scheme.scheme_code)
          ] || [];

        return clients.some(
          (client) => {

            const clientText = [
              client.invester_name,
              client.pan,
              client.cost_amount,
              client.current_value,
              client.profit_loss,
              client.absolute_return,
              client.xirr,
            ]
              .map((v) =>
                String(v ?? "")
                  .toLowerCase()
              )
              .join(" ");

            if (clientText.includes(q)) {
              return true;
            }

            const folioKey =
              getClientKey(
                scheme.scheme_code,
                client.pan
              );

            const folios =
              folioData[folioKey] || [];

            return folios.some(
              (folio) => {

                const folioText = [
                  folio.folio_number,
                  folio.purchase_units,
                  folio.purchase_amount,
                  folio.redemption_units,
                  folio.redemption_amount,
                  folio.balance_units,
                  folio.balance_value,
                  folio.profit_loss,
                  folio.absolute_return,
                  folio.xirr,
                ]
                  .map((v) =>
                    String(v ?? "")
                      .toLowerCase()
                  )
                  .join(" ");

                if (folioText.includes(q)) {
                  return true;
                }

                const txKey =
                  getFolioKey(
                    scheme.scheme_code,
                    client.pan,
                    folio.folio_number
                  );

                const transactions =
                  transactionData[txKey] || [];

                return transactions.some(
                  (tx) => {

                    const txText = [
                      tx.tran_date,
                      tx.nav_date,
                      tx.tran_no,
                      tx.units,
                      tx.amount,
                      tx.nav,
                      tx.tran_sign,
                      tx.tran_type,
                      tx.tran_description,
                      tx.remarks,
                      tx.balance_units,
                    ]
                      .map((v) =>
                        String(v ?? "")
                          .toLowerCase()
                      )
                      .join(" ");

                    return txText.includes(q);
                  }
                );
              }
            );
          }
        );
      });
    };


  // ==========================================================
  // CLIENT FILTER
  // ==========================================================

  const getFilteredClients =
    (schemeCode) => {

      const rows =
        clientData[
          String(schemeCode)
        ] || [];

      const q =
        searchText
          .trim()
          .toLowerCase();

      if (!q) {
        return rows;
      }

      return rows.filter((client) => {

        const clientText = [
          client.invester_name,
          client.pan,
          client.cost_amount,
          client.current_value,
          client.profit_loss,
          client.absolute_return,
          client.xirr,
        ]
          .map((v) =>
            String(v ?? "")
              .toLowerCase()
          )
          .join(" ");

        if (clientText.includes(q)) {
          return true;
        }

        const folioKey =
          getClientKey(
            schemeCode,
            client.pan
          );

        const folios =
          folioData[folioKey] || [];

        return folios.some((folio) => {

          const folioText = [
            folio.folio_number,
            folio.purchase_units,
            folio.purchase_amount,
            folio.redemption_units,
            folio.redemption_amount,
            folio.balance_units,
            folio.balance_value,
            folio.profit_loss,
            folio.absolute_return,
            folio.xirr,
          ]
            .map((v) =>
              String(v ?? "")
                .toLowerCase()
            )
            .join(" ");

          if (folioText.includes(q)) {
            return true;
          }

          const txKey =
            getFolioKey(
              schemeCode,
              client.pan,
              folio.folio_number
            );

          const transactions =
            transactionData[txKey] || [];

          return transactions.some(
            (tx) => {

              const txText = [
                tx.tran_date,
                tx.nav_date,
                tx.tran_no,
                tx.units,
                tx.amount,
                tx.nav,
                tx.tran_sign,
                tx.tran_type,
                tx.tran_description,
                tx.remarks,
                tx.balance_units,
              ]
                .map((v) =>
                  String(v ?? "")
                    .toLowerCase()
                )
                .join(" ");

              return txText.includes(q);
            }
          );
        });
      });
    };


  // ==========================================================
  // DATE FORMATTER
  // ==========================================================

  const formatDate = (value) => {

    if (!value) {
      return "-";
    }

    const raw =
      String(value).trim();

    // YYYY-MM-DD or YYYY-MM-DD HH:MM:SS
    const datePart =
      raw.substring(0, 10);

    const parts =
      datePart.split("-");

    if (
      parts.length === 3 &&
      parts[0].length === 4
    ) {

      const date =
        new Date(
          Number(parts[0]),
          Number(parts[1]) - 1,
          Number(parts[2])
        );

      if (!Number.isNaN(date.getTime())) {

        return date.toLocaleDateString(
          "en-GB",
          {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }
        );
      }
    }

    return raw;
  };


  // ==========================================================
  // UNIT FORMATTER
  // ==========================================================

  const formatUnits = (value) => {

    const num =
      Number(value);

    if (!Number.isFinite(num)) {
      return "-";
    }

    return num.toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }
    );
  };


  // ==========================================================
  // NAV FORMATTER
  // ==========================================================

  const formatNav = (value) => {

    const num =
      Number(value);

    if (!Number.isFinite(num)) {
      return "-";
    }

    return num.toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 3,
        maximumFractionDigits: 4,
      }
    );
  };


  // ==========================================================
  // TRANSACTION COLUMNS
  // Final Level:
  // Folio -> Transactions
  // ==========================================================

  const transactionColumns = [

    {
      title: "Tran Date",
      dataIndex: "tran_date",
      key: "tran_date",
      align: "center",
      width: 135,

      render: (value) =>
        formatDate(value),
    },

    {
      title: "NAV Date",
      dataIndex: "nav_date",
      key: "nav_date",
      align: "center",
      width: 135,

      render: (value) =>
        formatDate(value),
    },

    {
      title: "Tran No",
      dataIndex: "tran_no",
      key: "tran_no",
      width: 130,

      render: (value) => (
        <Text
          style={{
            fontWeight: 500,
          }}
        >
          {value || "-"}
        </Text>
      ),
    },

    {
      title: "Units",
      dataIndex: "units",
      key: "units",
      align: "right",
      width: 125,

      render: (value) =>
        formatUnits(value),
    },

    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 145,

      render: (value) =>
        formatMoney(value),
    },

    {
      title: "NAV",
      dataIndex: "nav",
      key: "nav",
      align: "right",
      width: 115,

      render: (value) =>
        formatNav(value),
    },

    {
      title: "Type",
      dataIndex: "tran_sign",
      key: "tran_sign",
      align: "center",
      width: 110,

      render: (value) => {

        const type =
          String(value || "")
            .trim()
            .toUpperCase();

        return (
          <Text
            style={{
              color:
                type === "CREDIT"
                  ? "#389e0d"
                  : type === "DEBIT"
                  ? "#cf1322"
                  : undefined,

              fontWeight: 600,
            }}
          >
            {type || "-"}
          </Text>
        );
      },
    },

    {
      title: "Balance Units",
      dataIndex: "balance_units",
      key: "balance_units",
      align: "right",
      width: 135,

      render: (value) =>
        formatUnits(value),
    },

  ];


  // ==========================================================
  // RENDER TRANSACTIONS
  // ==========================================================

  const renderTransactions =
    (
      schemeCode,
      pan,
      folioNumber
    ) => {

      const key =
        getFolioKey(
          schemeCode,
          pan,
          folioNumber
        );


      if (transactionLoading[key]) {

        return (
          <div
            style={{
              padding: 22,
              textAlign: "center",
            }}
          >
            <Spin tip="Loading Transactions..." />
          </div>
        );
      }


      if (transactionErrors[key]) {

        return (
          <Alert
            type="error"
            showIcon
            message="Unable to load transactions"
            description={
              transactionErrors[key]
            }
          />
        );
      }


      const rows =
        transactionData[key] || [];


      if (!rows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No transactions found for this folio in this scheme."
          />
        );
      }


      return (
        <div
          style={{
            padding: isMobile ? `8px ${deepNestedPad}px 10px` : "12px 14px 16px 36px",
            background: "#fffbe6",
          }}
        >

          <Table
            size="small"
            bordered
            pagination={false}

            rowKey={(record) =>
              record.id ||
              `${record.tran_no}-${record.nav_date}-${record.tran_date}-${record.units}`
            }

            columns={
              transactionColumns
            }

            dataSource={
              rows
            }

            scroll={{
              x: 1050,
            }}

            style={{
              background: "#fff",
            }}
          />

        </div>
      );
    };


  // ==========================================================
  // FOLIO COLUMNS
  //
  // Main grouped headers:
  //
  // Total Purchases
  //    -> Units
  //    -> Amount
  //
  // Redemptions
  //    -> Units
  //    -> Amount
  //
  // Balance
  //    -> Units
  //    -> Value
  //
  // ==========================================================

  const folioColumns = [

    // --------------------------------------------------------
    // EXPAND BUTTON
    // --------------------------------------------------------

    {
      title: "",
      key: "expand",
      width: 48,
      align: "center",

      render: (_, record) => {

        const schemeCode =
          record._schemeCode;

        const pan =
          record._pan;

        const key =
          getFolioKey(
            schemeCode,
            pan,
            record.folio_number
          );

        const expanded =
          !!expandedFolios[key];

        return (
          <Button
            type="text"

            icon={
              expanded
                ? <MinusSquareOutlined />
                : <PlusSquareOutlined />
            }

            onClick={() =>
              toggleFolio(
                schemeCode,
                pan,
                record.folio_number
              )
            }
          />
        );
      },
    },


    // --------------------------------------------------------
    // FOLIO NUMBER
    // --------------------------------------------------------

    {
      title: "Folio Number",
      dataIndex: "folio_number",
      key: "folio_number",
      width: 170,

      render: (value, record) => (
        <Space>

          <FolderOpenOutlined />

          <Button
            type="link"

            onClick={() =>
              toggleFolio(
                record._schemeCode,
                record._pan,
                record.folio_number
              )
            }

            style={{
              padding: 0,
              height: "auto",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            {value || "-"}
          </Button>

        </Space>
      ),
    },


    // --------------------------------------------------------
    // TOTAL PURCHASES
    // --------------------------------------------------------

    {
      title: "Total Purchases",

      children: [

        {
          title: "Units",
          dataIndex: "purchase_units",
          key: "purchase_units",
          align: "right",
          width: 125,

          render: (value) =>
            formatUnits(value),
        },

        {
          title: "Amount",
          dataIndex: "purchase_amount",
          key: "purchase_amount",
          align: "right",
          width: 145,

          render: (value) =>
            formatMoney(value),
        },

      ],
    },


    // --------------------------------------------------------
    // REDEMPTIONS
    // --------------------------------------------------------

    {
      title: "Redemptions",

      children: [

        {
          title: "Units",
          dataIndex: "redemption_units",
          key: "redemption_units",
          align: "right",
          width: 125,

          render: (value) =>
            formatUnits(value),
        },

        {
          title: "Amount",
          dataIndex: "redemption_amount",
          key: "redemption_amount",
          align: "right",
          width: 145,

          render: (value) =>
            formatMoney(value),
        },

      ],
    },


    // --------------------------------------------------------
    // BALANCE
    // --------------------------------------------------------

    {
      title: "Balance",

      children: [

        {
          title: "Units",
          dataIndex: "balance_units",
          key: "balance_units",
          align: "right",
          width: 125,

          render: (value) =>
            formatUnits(value),
        },

        {
          title: "Value",
          dataIndex: "balance_value",
          key: "balance_value",
          align: "right",
          width: 145,

          render: (value) =>
            formatMoney(value),
        },

      ],
    },


    // --------------------------------------------------------
    // PROFIT / LOSS
    // --------------------------------------------------------

    {
      title: "Profit / Loss",
      dataIndex: "profit_loss",
      key: "profit_loss",
      align: "right",
      width: 145,

      render: (value) => (
        <ReturnValue value={value} />
      ),
    },


    // --------------------------------------------------------
    // ABSOLUTE
    // --------------------------------------------------------

    {
      title: "Absolute",
      dataIndex: "absolute_return",
      key: "absolute_return",
      align: "right",
      width: 110,

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },


    // --------------------------------------------------------
    // XIRR
    // --------------------------------------------------------

    {
      title: "XIRR",
      dataIndex: "xirr",
      key: "xirr",
      align: "right",
      width: 100,

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },

  ];


  // ==========================================================
  // RENDER FOLIOS
  // Client -> Folio -> Transaction
  // ==========================================================

  const renderFolios =
    (schemeCode, pan) => {

      const key =
        getClientKey(
          schemeCode,
          pan
        );


      if (folioLoading[key]) {

        return (
          <div
            style={{
              padding: 20,
              textAlign: "center",
            }}
          >
            <Spin tip="Loading Folios..." />
          </div>
        );
      }


      if (folioErrors[key]) {

        return (
          <Alert
            type="error"
            showIcon
            message="Unable to load folios"
            description={
              folioErrors[key]
            }
          />
        );
      }


      const rawRows =
        folioData[key] || [];


      if (!rawRows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No folio data found for this client in this scheme."
          />
        );
      }


      // Parent values injected into each folio row.
      // API format remains untouched.

      const rows =
        rawRows.map((row) => ({
          ...row,

          _schemeCode:
            schemeCode,

          _pan:
            pan,
        }));


      return (
        <div
          style={{
            padding: isMobile ? `8px ${nestedPad}px 10px` : "12px 16px 16px 42px",
            background: "#f0f5ff",
          }}
        >

          <Table
            size="small"
            bordered
            pagination={false}

            rowKey={(record) =>
              `${schemeCode}-${pan}-${record.folio_number}`
            }

            columns={
              folioColumns
            }

            dataSource={
              rows
            }

            scroll={{
              x: 1450,
            }}

            style={{
              background: "#fff",
            }}

            onRow={(record) => ({ onClick: () => pushDrill({ level: "transactions", schemeCode, pan, folioNumber: record.folio_number }) })}
          />

        </div>
      );
    };


  // ==========================================================
  // CLIENT COLUMNS
  // ==========================================================

  const getClientColumns =
    (schemeCode) => [

      {
        title: "",
        key: "expand",
        width: 48,
        align: "center",

        render: (_, record) => {

          const key =
            getClientKey(
              schemeCode,
              record.pan
            );

          const expanded =
            !!expandedClients[key];

          return (
            <Button
              type="text"

              icon={
                expanded
                  ? <MinusSquareOutlined />
                  : <PlusSquareOutlined />
              }

              onClick={() =>
                toggleClient(
                  schemeCode,
                  record.pan
                )
              }
            />
          );
        },
      },

      {
        title: "Invester Name",
        dataIndex: "invester_name",
        key: "invester_name",

        render: (value, record) => (
          <Space>

            <UserOutlined />

            <Button
              type="link"

              onClick={() =>
                toggleClient(
                  schemeCode,
                  record.pan
                )
              }

              style={{
                padding: 0,
                height: "auto",
                fontWeight: 600,
                textAlign: "left",
              }}
            >
              {value || "Client"}
            </Button>

          </Space>
        ),
      },

      {
        title: "PAN",
        dataIndex: "pan",
        key: "pan",
        align: "center",

        render: (value) => (
          <Text
            style={{
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {value || "-"}
          </Text>
        ),
      },

      {
        title: "Cost Amount",
        dataIndex: "cost_amount",
        key: "cost_amount",
        align: "right",

        render: (value) =>
          formatMoney(value),
      },

      {
        title: "Current Value",
        dataIndex: "current_value",
        key: "current_value",
        align: "right",

        render: (value) =>
          formatMoney(value),
      },

      {
        title: "Profit / Loss",
        dataIndex: "profit_loss",
        key: "profit_loss",
        align: "right",

        render: (value) => (
          <ReturnValue value={value} />
        ),
      },

      {
        title: "Absolute",
        dataIndex: "absolute_return",
        key: "absolute_return",
        align: "right",

        render: (value) => (
          <ReturnValue
            value={value}
            percent
          />
        ),
      },

      {
        title: "XIRR",
        dataIndex: "xirr",
        key: "xirr",
        align: "right",

        render: (value) => (
          <ReturnValue
            value={value}
            percent
          />
        ),
      },
    ];


  // ==========================================================
  // CLIENT EXPANSION
  // ==========================================================

  const renderClients =
    (schemeCode) => {

      const key =
        String(schemeCode);

      if (clientLoading[key]) {

        return (
          <div
            style={{
              padding: 25,
              textAlign: "center",
            }}
          >
            <Spin tip="Loading Clients..." />
          </div>
        );
      }

      if (clientErrors[key]) {

        return (
          <Alert
            type="error"
            showIcon
            message="Unable to load clients"
            description={
              clientErrors[key]
            }
          />
        );
      }

      const allRows =
        clientData[key] || [];

      const rows =
        getFilteredClients(
          schemeCode
        );

      if (!allRows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No client data found for this scheme."
          />
        );
      }

      if (!rows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No client matches current search."
          />
        );
      }

      return (
        <div
          style={{
            padding: isMobile ? `8px ${nestedPad}px 10px` : "12px 16px 16px 30px",
            background: "#fafafa",
          }}
        >

          <Table
            size="small"
            bordered
            pagination={false}

            rowKey={(record) =>
              `${record.scheme_code || schemeCode}-${record.pan}`
            }

            columns={
              getClientColumns(
                schemeCode
              )
            }

            dataSource={rows}

            style={{
              background: "#fff",
            }}

            onRow={(record) => ({ onClick: () => pushDrill({ level: "folios", schemeCode, pan: record.pan }) })}
          />

        </div>
      );
    };
  // ==========================================================
  // SCHEME COLUMNS
  // ==========================================================

  const schemeColumns = [

    {
      title: "",
      key: "expand",
      width: 48,
      align: "center",

      render: (_, record) => {

        const key =
          String(record.scheme_code);

        const expanded =
          !!expandedSchemes[key];

        return (
          <Button
            type="text"

            icon={
              expanded
                ? <MinusSquareOutlined />
                : <PlusSquareOutlined />
            }

            onClick={() =>
              toggleScheme(
                record.scheme_code
              )
            }
          />
        );
      },
    },

    {
      title: "Scheme Name",
      dataIndex: "scheme_name",
      key: "scheme_name",

      render: (value, record) => (
        <Space>

          <FundOutlined />

          <Button
            type="link"

            onClick={() =>
              toggleScheme(
                record.scheme_code
              )
            }

            style={{
              padding: 0,
              height: "auto",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            {value ||
              `Scheme ${record.scheme_code}`}
          </Button>

        </Space>
      ),
    },

    {
      title: "Cost Amount",
      dataIndex: "cost_amount",
      key: "cost_amount",
      align: "right",

      render: (value) =>
        formatMoney(value),
    },

    {
      title: "Current Value",
      dataIndex: "current_value",
      key: "current_value",
      align: "right",

      render: (value) =>
        formatMoney(value),
    },

    {
      title: "Profit / Loss",
      dataIndex: "profit_loss",
      key: "profit_loss",
      align: "right",

      render: (value) => (
        <ReturnValue value={value} />
      ),
    },

    {
      title: "Absolute",
      dataIndex: "absolute_return",
      key: "absolute_return",
      align: "right",

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },

    {
      title: "XIRR",
      dataIndex: "xirr",
      key: "xirr",
      align: "right",

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },
  ];


  // ==========================================================
  // SCHEME EXPANSION
  // AMC -> Scheme -> Client
  // ==========================================================

  const renderSchemes =
    (amcName) => {

      if (schemeLoading[amcName]) {

        return (
          <div
            style={{
              padding: 30,
              textAlign: "center",
            }}
          >
            <Spin tip="Loading Schemes..." />
          </div>
        );
      }


      if (schemeErrors[amcName]) {

        return (
          <Alert
            type="error"
            showIcon
            message="Unable to load schemes"
            description={
              schemeErrors[amcName]
            }
          />
        );
      }


      const allRows =
        schemeData[amcName] || [];


      const rows =
        getFilteredSchemes(
          amcName
        );


      if (!allRows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No Scheme data found for this AMC."
          />
        );
      }


      if (!rows.length) {

        return (
          <Alert
            type="info"
            showIcon
            message="No scheme matches current search."
          />
        );
      }


      return (
        <div
          style={{
            padding: isMobile ? `8px ${nestedPad}px 10px` : "14px 18px 20px 18px",
            background: "#fafafa",
            width: "100%",
          }}
        >

          <Table
            size="small"
            bordered
            pagination={false}

            rowKey={(record) =>
              `${amcName}-${record.scheme_code}`
            }

            columns={
              schemeColumns
            }

            dataSource={
              rows
            }

            style={{
              background: "#ffffff",
              width: "100%",
            }}

            scroll={{
              x: 1050,
            }}

            onRow={(record) => ({ onClick: () => pushDrill({ level: "clients", amcName, schemeCode: record.scheme_code }) })}
          />

        </div>
      );
    };


  // ==========================================================
  // AMC COLUMNS
  // ==========================================================

  const amcColumns = [

    {
      title: "",
      key: "expand",
      width: 48,
      align: "center",

      render: (_, record) => {

        const expanded =
          !!expandedAMCs[
            record.amc_name
          ];

        return (
          <Button
            type="text"

            icon={
              expanded
                ? <MinusSquareOutlined />
                : <PlusSquareOutlined />
            }

            onClick={() =>
              toggleAMC(
                record.amc_name
              )
            }
          />
        );
      },
    },

    {
      title: "AMC Name",
      dataIndex: "amc_name",
      key: "amc_name",

      render: (value, record) => (
        <Space>

          <BankOutlined />

          <Button
            type="link"

            onClick={() =>
              toggleAMC(
                record.amc_name
              )
            }

            style={{
              padding: 0,
              height: "auto",
              fontWeight: 600,
              fontSize: 15,
              textAlign: "left",
            }}
          >
            {value}
          </Button>

        </Space>
      ),
    },

    {
      title: "Cost Amount",
      dataIndex: "cost_amount",
      key: "cost_amount",
      align: "right",

      render: (value) =>
        formatMoney(value),
    },

    {
      title: "Current Value",
      dataIndex: "current_value",
      key: "current_value",
      align: "right",

      render: (value) =>
        formatMoney(value),
    },

    {
      title: "Profit / Loss",
      dataIndex: "profit_loss",
      key: "profit_loss",
      align: "right",

      render: (value) => (
        <ReturnValue value={value} />
      ),
    },

    {
      title: "Absolute",
      dataIndex: "absolute_return",
      key: "absolute_return",
      align: "right",

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },

    {
      title: "XIRR",
      dataIndex: "xirr",
      key: "xirr",
      align: "right",

      render: (value) => (
        <ReturnValue
          value={value}
          percent
        />
      ),
    },
  ];


  // ==========================================================
  // MAIN RENDER
  // ==========================================================

  // Load the selected drill level even when the user clicks anywhere on the row (not only the old + button).
  useEffect(() => {
    if (drillView.level === "schemes" && drillView.amcName && !Object.prototype.hasOwnProperty.call(schemeData, drillView.amcName)) loadSchemeData(drillView.amcName);
    if (drillView.level === "clients" && drillView.schemeCode && !Object.prototype.hasOwnProperty.call(clientData, String(drillView.schemeCode))) loadClientData(drillView.schemeCode);
    if (drillView.level === "folios" && drillView.schemeCode && drillView.pan) {
      const k = getClientKey(drillView.schemeCode, drillView.pan);
      if (!Object.prototype.hasOwnProperty.call(folioData, k)) loadFolioData(drillView.schemeCode, drillView.pan);
    }
    if (drillView.level === "transactions" && drillView.schemeCode && drillView.pan && drillView.folioNumber) {
      const k = getFolioKey(drillView.schemeCode, drillView.pan, drillView.folioNumber);
      if (!Object.prototype.hasOwnProperty.call(transactionData, k)) loadTransactionData(drillView.schemeCode, drillView.pan, drillView.folioNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillView]);

  const responsiveCss = `
    .amc-scheme-report-responsive .ant-table-wrapper { max-width: 100%; }
    .amc-scheme-report-responsive .ant-table-cell { white-space: nowrap; }
    .amc-scheme-report-responsive .ant-table-cell .ant-btn-link { white-space: normal; line-height: 1.25; }
    .amc-scheme-report-responsive .ant-table-content { -webkit-overflow-scrolling: touch; }
    @media (max-width: 767px) {
      .amc-scheme-report-responsive .ant-table { font-size: 12px; }
      .amc-scheme-report-responsive .ant-table-thead > tr > th,
      .amc-scheme-report-responsive .ant-table-tbody > tr > td { padding: 7px 6px !important; }
      .amc-scheme-report-responsive .ant-btn.ant-btn-text { min-width: 32px; min-height: 32px; padding: 4px; }
      .amc-scheme-report-responsive .ant-card { border-radius: 6px; }
      .amc-scheme-report-responsive .ant-table-expanded-row > td { padding: 0 !important; }
      .amc-scheme-report-responsive .ant-typography { font-size: 12px; }
    }
  `;

  return (

    <div
      className="amc-scheme-report-responsive"
      style={{
        padding: pagePadding,
        overflowX: "auto",
        background: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <style>{responsiveCss}</style>

      <Card bodyStyle={{ padding: isMobile ? 8 : isTablet ? 14 : 24 }}>

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div
          style={{
            marginBottom: 20,
          }}
        >

          {drillView.level !== "amcs" && (
            <Button icon={<ArrowLeftOutlined />} onClick={drillBack} style={{ marginBottom: 10 }}>
              {!isMobile ? "Back" : null}
            </Button>
          )}

          <Title
            level={isMobile ? 4 : 3}
            style={{
              marginBottom: 4,
              lineHeight: 1.25,
            }}
          >
            AMC & Scheme Wise Portfolio Report
          </Title>


        </div>


        {/* ====================================================
            GLOBAL SEARCH
        ==================================================== */}

        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: isMobile ? "stretch" : "flex-end",
            width: "100%",
          }}
        >

          <Input
            allowClear

            prefix={
              <SearchOutlined />
            }

            value={
              searchText
            }

            onChange={(e) =>
              setSearchText(
                e.target.value
              )
            }

            placeholder="Search AMC / Scheme / Client / PAN / Folio / Transaction..."

            style={{
              width: isMobile ? "100%" : 470,
              maxWidth: "100%",
            }}
          />

        </div>


        {/* ====================================================
            ERROR
        ==================================================== */}

        {amcError && (

          <Alert
            type="error"
            showIcon
            message="Unable to load report"
            description={
              amcError
            }
            style={{
              marginBottom: 15,
            }}
          />

        )}


        {/* ====================================================
            AMC TABLE
        ==================================================== */}

        {drillView.level === "schemes" ? renderSchemes(drillView.amcName) :
         drillView.level === "clients" ? renderClients(drillView.schemeCode) :
         drillView.level === "folios" ? renderFolios(drillView.schemeCode, drillView.pan) :
         drillView.level === "transactions" ? renderTransactions(drillView.schemeCode, drillView.pan, drillView.folioNumber) :
         amcLoading ? (

          <div
            style={{
              padding: isMobile ? 30 : 60,
              textAlign: "center",
            }}
          >

            <Spin
              size="large"
              tip="Loading AMC Portfolio..."
            />

          </div>

        ) : (

          <Table
            bordered
            pagination={false}

            rowKey={(record) =>
              record.amc_name
            }

            columns={
              amcColumns
            }

            dataSource={
              filteredAmcData
            }

            scroll={{
              x: 1050,
            }}

            style={{
              width: "100%",
            }}

            onRow={(record) => ({ onClick: () => pushDrill({ level: "schemes", amcName: record.amc_name }) })}
          />

        )}

      </Card>

    </div>
  );
}


// ============================================================
// EXPORT
// ============================================================

export default AMCSchemeReport;